import { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal, ModalCloseButton } from 'components/Shared';
import * as S from './styles';
import { useMutation } from '@tanstack/react-query';
import { useProduct } from 'hooks/api/general/useProduct';
import { Skeleton as SkeletonUI } from 'components/ui/skeleton';
import { PackageSearch } from 'lucide-react';
import { Skeleton } from './skeleton';
import { Formatter } from 'utils';

export interface DetailRef {
  showDetail: (data: any, companyIds: Array<string | number>) => void;
}

export const ItemDetail = forwardRef<DetailRef>((_, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const { getInfoCheckProduct } = useProduct();

  const {
    isPending,
    data = [],
    mutate,
    reset,
  } = useMutation({
    mutationFn: getInfoCheckProduct,
  });

  const closeModal = () => {
    setOpen(false);
    reset();
  };

  const openModal = (code: string, companyIds: Array<string | number>) => {
    setOpen(true);
    mutate({ code, companyIds });
  };

  useImperativeHandle(ref, () => ({
    showDetail: openModal,
  }));

  return (
    <Modal isOpen={open} onClickOutside={closeModal}>
      <S.ModalContent maxWidth="700px">
        <S.ModalHeader>
          <S.HeaderWrapper>
            {isPending ? (
              <div className="border-2 border-gray-200 border-l-gray-600 animate-spin w-5 h-5 rounded-3xl" />
            ) : (
              <PackageSearch size={24} />
            )}
            <S.Title>Produto em uso</S.Title>
            <S.SubTitle title={data?.[0]?.description}>
              {isPending ? (
                <SkeletonUI className="h-7 w-full" />
              ) : (
                `${data?.[0]?.code}-${data?.[0]?.description}`
              )}
            </S.SubTitle>
            <div className="w-full flex justify-end">
              <ModalCloseButton onClick={closeModal} />
            </div>
          </S.HeaderWrapper>
        </S.ModalHeader>
        <S.ModalBody>
          {isPending ? (
            <Skeleton />
          ) : data.length ? (
            <table className="border-collapse w-full">
              <thead className="font-GilroySemibold text-sm text-left text-gray-700">
                <tr className="[&>th]:p-1 [&>th]:pr-4 [&>th]:pb-2 [&>th]:whitespace-nowrap">
                  <th>ID</th>
                  <th>DATA</th>
                  <th title="Quantidade de vezes do produto incluído na mesma ordem">
                    QUANTIDADE
                  </th>
                  <th>TIPO DE ORDEM</th>
                  <th>CLIENTE</th>
                </tr>
              </thead>
              <tbody className="font-GilroyRegular text-sm">
                {data.map((info) => (
                  <tr className="[&>td]:p-1 [&>td]:pr-4 border border-white border-b-slate-200 last:border-none">
                    <td title="Id da solicitação">{info.order.id}</td>
                    <td title="Data agendamento">
                      {Formatter.date(info.order.scheduledAt)}
                    </td>
                    <td title="Qtd de lote pro mesmo produto">
                      {info.meta.countSameOrder}
                    </td>
                    <td>{info.order.orderType?.name}</td>
                    <td>
                      {info.company?.tradeName}-{info.company?.ie}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <></>
          )}
        </S.ModalBody>
        <S.ModalBody></S.ModalBody>
      </S.ModalContent>
    </Modal>
  );
});
